import type { DocPackInfo } from '@listening/api-contract/schemas';
import { useFlag } from '@listening/frontend/analytics/client';
import {
  AudioChunkPlayerContext,
  useDocPack,
} from '@listening/frontend/audio-chunk-player/core';
import {
  docPackEnabledWithOverride,
  prefetchDocPackPages,
  useReaderSourceOverride,
} from '@listening/frontend/doc-pack/source';
import { RegistryContext } from '@effect/atom-react';
import { useContextAndErrorIfNull } from '@listening/utils/react-helpers';
import { use, useEffect } from 'react';

/** How the player loads pack pages; a reader that loads them the same way
 * shares each page with it. */
export const useDocPackPageFetchMode = () =>
  useContextAndErrorIfNull(AudioChunkPlayerContext).docPackPageFetchMode;

// The platform-agnostic pack data layer and the per-document gate
// (`useDocPack`) live in @listening/frontend — shared with the mobile
// reader. This file keeps only the web-specific halves: the PostHog
// kill-switch flag that feeds the player's `docPackEnabled` seam, and the
// SSR-aware warm path.

// The dev/staging reader-source switcher (ReaderDevBadges) overrides the
// kill-switch flag. @see web-app-tooling.md §5.
export function useDocPackLoadingEnabled(): boolean {
  const override = useReaderSourceOverride();
  const flagEnabled = useFlag('disableDocPackLoading').value !== 'true';
  return docPackEnabledWithOverride(override, flagEnabled);
}

// Warms the pack read path from outside the (lazily-loaded) PDF view: the
// catalog resolves from the server-rendered page, and the first pages'
// blobs download while the PDF view's JS chunk is still loading, taking the
// pack reads off the first-paint critical path.
export function useWarmDocPack({
  audioId,
  docPack,
  initialPage,
}: {
  audioId: string;
  docPack: DocPackInfo | undefined;
  initialPage: number;
}) {
  const registry = use(RegistryContext);
  const fetchMode = useDocPackPageFetchMode();
  const { pack } = useDocPack({ id: audioId, doc_pack: docPack });
  useEffect(() => {
    if (!pack) return;
    prefetchDocPackPages(
      registry,
      { audioId, packUrl: pack.url, fetchMode },
      pack.catalog.pageCount,
      [initialPage, initialPage + 1, initialPage - 1, initialPage + 2],
    );
  }, [audioId, fetchMode, initialPage, pack, registry]);
}
